// =============================================================================
// app/src/lib/orcamentoDoc.ts — o orçamento para a FILA de impressão da loja.
//
// Não confundir com orcamentoPdf.ts: o PDF é o arquivo que se baixa/manda no
// WhatsApp; ESTE é o HTML autocontido que o agente da impressora (no computador
// do dono) transforma em papel. Convivem de propósito, como no vale a-ver.
//
// As MESMAS decisões de segurança de aVerDoc.ts, pelo mesmo motivo:
//  * todo valor vindo do banco passa por escaparHtml() — descrição de peça
//    diversa e nome de cliente são texto digitado por gente;
//  * CSP script-src 'none' no próprio documento: escape que falhar vira texto
//    na folha, nunca código.
//
// DADOS DA LOJA de @negocio (regra 5): campo não confirmado é null e NÃO sai
// no papel — nada de placeholder plausível em documento entregue a cliente.
// =============================================================================
import type { OrcamentoCompleto } from './consultas';
import { escaparHtml, S_LOGO } from './aVerDoc';
import { brl, dataHoraBR, qtd } from './formato';
// dataCurtaDeISO e não dataBR: valido_ate é DATA PURA, e new Date('YYYY-MM-DD')
// vira meia-noite UTC — em Rio Grande (UTC-3) imprimiria o dia ANTERIOR.
import { dataCurtaDeISO } from './orcamento';
import { negocio } from '@negocio';

/** Endereço em uma linha, só com o que está confirmado. */
export function enderecoEmLinha(): string {
  const e = negocio.endereco;
  const rua = [e.rua, e.numero].filter(Boolean).join(', ');
  return [rua, e.bairro, [negocio.cidade, negocio.uf].filter(Boolean).join('/'), e.cep]
    .filter(Boolean).join(' · ');
}

export function montarDocumentoOrcamento(o: OrcamentoCompleto): string {
  const fones = [negocio.telefoneFixo, negocio.whatsapp].filter(Boolean).join(' · ');
  const endereco = enderecoEmLinha();

  const linhas = [...o.itens]
    .sort((a, b) => a.ordem - b.ordem)
    .map((i) => `<tr>
      <td class="desc">${escaparHtml(i.descricao)}</td>
      <td class="num">${escaparHtml(qtd(Number(i.quantidade)))}</td>
      <td class="num">${escaparHtml(brl(Number(i.preco_unit)))}</td>
      <td class="num">${escaparHtml(brl(Number(i.subtotal)))}</td>
    </tr>`)
    .join('\n');

  const bruto = o.itens.reduce((s, i) => s + Number(i.subtotal), 0);
  const desconto = Number(o.desconto) || 0;

  // O mesmo recado do PDF — é o que evita discussão no balcão depois.
  const aviso = `Preços válidos até ${dataCurtaDeISO(o.valido_ate)}, sujeitos à disponibilidade em `
    + 'estoque. Este documento é um orçamento e não vale como comprovante de compra.';

  // Sem <script> aqui dentro, de propósito — ver o cabeçalho.
  return `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy"
      content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; script-src 'none'; form-action 'none'">
<title>${escaparHtml(o.codigo)} — Orçamento</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  /* UMA folha A4 (1123 px CSS): o que passar disso o agente corta. A tabela
     de itens é a única parte que cresce — ~30 itens cabem; acima disso o
     caminho é o PDF, não a fila. */
  body { font-family: Arial, Helvetica, sans-serif; color: #111; padding: 10mm; }
  header { display: flex; justify-content: space-between; align-items: flex-start;
           border-bottom: 2px solid #111; padding-bottom: 3mm; margin-bottom: 4mm; }
  /* O conjunto da marca é o MESMO do vale (aVerDoc): símbolo + SERGINHO em
     900 itálico + AUTO PEÇAS espaçado. Os dois documentos saem da mesma
     impressora e vão para a mesma mão — divergir na marca entre eles é o tipo
     de detalhe que o cliente nota sem saber dizer o quê. Um degrau maior que
     o vale (20/10 contra 18/9) porque o cabeçalho do orçamento já roda nessa
     escala. */
  .loja .marca .nome { font-weight: 900; font-style: italic; font-size: 20px;
                       margin-left: 6px; letter-spacing: .5px; }
  .loja .marca .sub { font-size: 10px; letter-spacing: 2px; margin-left: 6px; color: #555; }
  .loja p { font-size: 10px; color: #444; margin-top: 1mm; }
  .doc { text-align: right; }
  .doc .titulo { display: block; font-size: 11px; letter-spacing: 2px; }
  .doc .codigo { display: block; font-weight: 900; font-size: 20px; font-family: monospace; }
  .doc p { font-size: 10px; color: #444; margin-top: 1mm; }
  .quem { display: flex; justify-content: space-between; gap: 6mm; margin-bottom: 4mm; }
  .quem p { font-size: 12px; }
  .quem .rot { font-size: 9.5px; color: #555; text-transform: uppercase; letter-spacing: 1px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 3mm; }
  th { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #555;
       text-align: left; border-bottom: 2px solid #111; padding: 1.5mm 1mm; }
  th.num, td.num { text-align: right; white-space: nowrap; }
  td { padding: 1.6mm 1mm; font-size: 12px; border-bottom: 1px solid #ddd; vertical-align: top; }
  td.desc small { display: block; color: #777; font-family: monospace; font-size: 9.5px; }
  .totais { margin-left: auto; width: 70mm; margin-bottom: 4mm; }
  .totais div { display: flex; justify-content: space-between; font-size: 12px; padding: 1mm 0; }
  .totais .total { border-top: 2px solid #111; font-size: 17px; font-weight: 900; padding-top: 2mm; }
  .aviso { font-size: 10px; color: #333; line-height: 1.45; border-top: 1px solid #ccc; padding-top: 2.5mm; }
</style></head>
<body>
  <header>
    <div class="loja">
      <div class="marca">${S_LOGO}<span class="nome">SERGINHO</span><span
        class="sub">AUTO PEÇAS</span></div>
      ${negocio.cnpj ? `<p>CNPJ ${escaparHtml(negocio.cnpj)}</p>` : ''}
      ${endereco ? `<p>${escaparHtml(endereco)}</p>` : ''}
      ${fones ? `<p>${escaparHtml(fones)}</p>` : ''}
    </div>
    <div class="doc">
      <span class="titulo">ORÇAMENTO</span>
      <span class="codigo">${escaparHtml(o.codigo)}</span>
      <p>Emitido em ${escaparHtml(dataHoraBR(o.created_at))}</p>
      <p><strong>Válido até ${escaparHtml(dataCurtaDeISO(o.valido_ate))}</strong></p>
    </div>
  </header>

  <div class="quem">
    <div>
      <p class="rot">Cliente</p>
      <p>${escaparHtml(o.cliente_nome ?? 'Balcão')}${
        o.cliente_contato ? ` · ${escaparHtml(o.cliente_contato)}` : ''}</p>
    </div>
    ${o.veiculo ? `<div>
      <p class="rot">Veículo</p>
      <p>${escaparHtml(o.veiculo)}</p>
    </div>` : ''}
    ${o.vendedor ? `<div>
      <p class="rot">Vendedor</p>
      <p>${escaparHtml(o.vendedor)}</p>
    </div>` : ''}
  </div>

  <table>
    <thead><tr>
      <th>Peça</th><th class="num">Qtd</th><th class="num">Unitário</th><th class="num">Total</th>
    </tr></thead>
    <tbody>
${linhas}
    </tbody>
  </table>

  <div class="totais">
    ${desconto > 0 ? `<div><span>Itens</span><span>${escaparHtml(brl(bruto))}</span></div>
    <div><span>Desconto</span><span>−${escaparHtml(brl(desconto))}</span></div>` : ''}
    <div class="total"><span>Total</span><span>${escaparHtml(brl(Number(o.total)))}</span></div>
  </div>

  <p class="aviso">${escaparHtml(aviso)}</p>
</body></html>`;
}
